import React, { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import { changePlan } from '../../lib/billing';

/**
 * Phase 23 — Plan picker modal. Shows the 3 plans; current plan is tagged;
 * other two are switchable. Confirms with a proration note before calling
 * the backend. On success emits ``onSwitched(result)``.
 */
export default function PlanPicker({ plans, currentPlanCode, isAdminOverride, onClose, onSwitched, onError }) {
  const { colors } = useTheme();
  const [confirming, setConfirming] = useState(null); // plan_code
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const submit = async (code) => {
    setBusy(true);
    try {
      const r = await changePlan(code);
      onSwitched?.(r);
    } catch (e) {
      onError?.(e?.detail || 'Could not change plan');
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  return (
    <div
      data-testid="plan-picker"
      role="dialog" aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(10,10,10,0.55)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.surface, borderRadius: 20, maxWidth: 940, width: '100%',
          padding: 28, boxShadow: '0 40px 100px -30px rgba(0,0,0,0.3)',
          maxHeight: '92vh', overflow: 'auto', position: 'relative',
        }}
      >
        <button
          onClick={onClose} data-testid="plan-picker-close" aria-label="Close"
          style={{ position: 'absolute', top: 14, right: 14, background: 'transparent', border: 'none', cursor: 'pointer', color: colors.textMuted, padding: 6 }}
        >
          <X size={18} />
        </button>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: colors.text, letterSpacing: '-0.02em' }}>Switch plan</h3>
        <p style={{ marginTop: 6, marginBottom: 20, color: colors.textMuted, fontSize: 14 }}>
          You'll be charged or credited the pro-rated difference immediately. Your trial end date stays the same.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {plans.map((p) => {
            const isCurrent = p.code === currentPlanCode;
            return (
              <div
                key={p.code}
                data-testid={`plan-picker-card-${p.code}`}
                style={{
                  padding: 22, borderRadius: 16,
                  border: `${isCurrent ? 2 : 1}px solid ${isCurrent ? colors.accent : colors.border}`,
                  background: colors.surface,
                  boxShadow: isCurrent ? `0 20px 40px -20px ${colors.accent}` : 'none',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ color: colors.textMuted, fontWeight: 700, fontSize: 13 }}>{p.name}</div>
                  {isCurrent && (
                    <span style={{ padding: '2px 10px', borderRadius: 999, background: colors.accentSoft, color: colors.accent, fontWeight: 700, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Current</span>
                  )}
                </div>
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', color: colors.text }}>${p.amount.toFixed(0)}</span>
                  <span style={{ color: colors.textMuted, fontSize: 13 }}>{p.currency} / {p.interval}</span>
                </div>
                <ul style={{ paddingLeft: 0, listStyle: 'none', margin: '14px 0 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {p.features.slice(0, 5).map((f) => (
                    <li key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: colors.text, fontSize: 13, lineHeight: 1.4 }}>
                      <Check size={14} color={colors.accent} style={{ marginTop: 2 }} />{f}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  disabled={isCurrent || busy}
                  onClick={() => setConfirming(p.code)}
                  data-testid={`plan-picker-switch-${p.code}`}
                  style={{
                    width: '100%', marginTop: 16, padding: '10px 14px', borderRadius: 10,
                    border: 'none', fontWeight: 700, fontSize: 13,
                    background: isCurrent ? colors.border : colors.accent,
                    color: isCurrent ? colors.textMuted : 'white',
                    cursor: isCurrent || busy ? 'not-allowed' : 'pointer',
                    opacity: busy && !isCurrent ? 0.6 : 1,
                  }}
                >
                  {isCurrent ? 'Current plan' : `Switch to ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>

        {confirming && (
          <div style={{ marginTop: 20, padding: 16, borderRadius: 12, background: colors.surfaceSoft || colors.accentSoft, border: `1px solid ${colors.border}` }}>
            <div style={{ color: colors.text, fontWeight: 700, fontSize: 14 }}>
              Switch to {plans.find((p) => p.code === confirming)?.name}?
            </div>
            <div style={{ color: colors.textMuted, fontSize: 13, marginTop: 4 }}>
              You'll be charged or credited the pro-rated difference immediately. Confirm to continue.
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirming(null)} disabled={busy} style={{ padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: 10, background: 'transparent', color: colors.text, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button
                  type="button" disabled={busy || isAdminOverride}
                  onClick={() => submit(confirming)}
                  data-testid="plan-picker-confirm"
                  style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: isAdminOverride ? colors.border : colors.accent, color: isAdminOverride ? colors.textMuted : 'white', cursor: busy || isAdminOverride ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}
                >{isAdminOverride ? "Admin accounts don't require a plan" : (busy ? 'Switching…' : 'Confirm switch')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
